import {groupBy, some} from 'lodash';
import moment, {MomentInput} from 'moment-timezone';

import {EMPTY_OBJECT} from '~/shared/consts/commonConsts';
import {PaymentMethodTypes} from '~/shared/consts/paymentConsts';
import {getCurrentLocation} from '~/shared/router';
import {OrderWithMenuVisibility} from '~/shared/store/models/User/UserReport';
import {Order} from '~/shared/services/apiService/models';

export type GroupParentType = {
  parentId: string;
  dateLabel?: string;
  items?: OrderWithMenuVisibility[];
  employeePart: number;
  employerPart: number;
  dailySum: number;
  orderDate?: string;
};

const sortByDate = (a: {orderDate?: MomentInput}, b: {orderDate?: MomentInput}) =>
  moment(b.orderDate).diff(moment(a.orderDate));

// TODO: use generatePropsForMenuList for TransactionTable
//       - move to userReport general (utils|hooks)
export const generatePropsForMenuList = (order: Order, config: IGetOrdersGroupedByDate['config']) => {
  const location = getCurrentLocation();

  const {showTransferDebit, debitTransfersSentRequests} = config || EMPTY_OBJECT;
  const {
    orderId,
    orderDate,
    orderType,
    payments,
    review,
    existTransferRequest,
    total,
    inoviceUrls,
    restaurantReviewsDisabled,
  } = order;

  const _dateBias = new URLSearchParams(location.search).get('dateBias');
  const dateBias = Number(_dateBias);
  const isTerminalOrderType = orderType === 'terminal';

  const orderDaysAgo = moment().diff(orderDate, 'days');
  const allowedToSetReview = orderDaysAgo <= 14;
  const isOrderPaidWithMoneyCard = some(payments, {paymentMethod: PaymentMethodTypes.MONEY_CARD});
  const _existTransferRequest = some(debitTransfersSentRequests, {orderId});
  return {
    shouldShowOrderMenuButton: !isTerminalOrderType,
    shouldShowAddAReviewButton: review?.reviewId === 0 && !restaurantReviewsDisabled && allowedToSetReview,
    shouldShowEditAReviewButton: review?.reviewId !== 0 && !restaurantReviewsDisabled && allowedToSetReview,
    shouldShowDisplayReviewButton: review?.reviewId !== 0 && !restaurantReviewsDisabled && !allowedToSetReview,
    shouldShowDebitTransferMenuButton:
      showTransferDebit &&
      isOrderPaidWithMoneyCard &&
      isTerminalOrderType &&
      !existTransferRequest &&
      !_existTransferRequest &&
      dateBias === 0 &&
      (total as number) > 0,
    shouldShowDownloadInvoiceMenuButton: inoviceUrls?.length > 0,
  };
};

export const getOrdersGroupedByDate = (
  orders: IGetOrdersGroupedByDate['orderList'],
  config: IGetOrdersGroupedByDate['config'],
): GroupParentType[] | undefined => {
  if (!orders) {
    return;
  }

  const grouped = groupBy(orders.sort(sortByDate), 'orderDateStr');

  return Object.keys(grouped).map(key => {
    const items = grouped[key].map(item => ({
      ...item,
      ...generatePropsForMenuList(item, config),
    }));

    return {
      parentId: key,
      dateLabel: key,
      employeePart: items[0].employeePart,
      employerPart: items[0].employerPart,
      orderDate: items[0].orderDate,
      items,
      dailySum: items[0].dailySum || 0,
    };
  });
};

interface IGetOrdersGroupedByDate {
  orderList?: Order[];
  config?: {
    showTransferDebit: boolean;
    debitTransfersSentRequests: Record<string, any>[];
  };
}
